import { showNotification } from "components/v2/notification/notification";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { downloadStream, getMatchJobDetails, getStreamDownloadLink } from "store/stream/actions";

// import { clearChunks, loadChunks, saveChunk } from "utils/indexed-db";
import { DownloadMatchContext } from "./context";

// Utility function for local storage operations
const useStorageOperations = () => {
  const getDownloads = () => JSON.parse(localStorage.getItem("downloads") || "{}");
  const setDownloads = (downloads) => localStorage.setItem("downloads", JSON.stringify(downloads));
  const getProgress = (streamId, id) => localStorage.getItem(`downloadProgress_${streamId}_${id}`);
  const setProgress = (streamId, id, progress) => localStorage.setItem(`downloadProgress_${streamId}_${id}`, progress);
  const clearProgress = (streamId, id) => localStorage.removeItem(`downloadProgress_${streamId}_${id}`);
  const getEntityId = () => localStorage.getItem("entityId");

  return { getDownloads, setDownloads, getProgress, setProgress, clearProgress, getEntityId };
};

export const DownloadMatchProvider = ({ children, downloadStream, getStreamDownloadLink, getMatchJobDetails }) => {
  const storageOperations = useStorageOperations();
  const initialDownloads = useMemo(() => storageOperations.getDownloads(), [storageOperations]);

  const [downloads, setDownloads] = useState(initialDownloads);
  const [isDownloadListVisible, setIsDownloadListVisible] = useState(false);

  const toggleDownloadList = useCallback(() => {
    setIsDownloadListVisible((prev) => !prev);
  }, []);

  const getDownloadLink = useCallback(
    async ({ streamId, id }) => {
      try {
        const { payload } = await getStreamDownloadLink({
          payload: { entityId: storageOperations.getEntityId(), streamId, id },
        });
        return payload?.data?.data?.url;
      } catch (error) {
        console.error(error);
        return "";
      }
    },
    [getStreamDownloadLink, storageOperations],
  );

  const updateDownloadState = useCallback(
    (streamId, id, updateData) => {
      setDownloads((prev) => {
        const newDownloads = {
          ...prev,
          [`${streamId}_${id}`]: {
            ...prev[`${streamId}_${id}`],
            ...updateData,
          },
        };
        if (!("progress" in updateData) || Object.keys(updateData).length > 1) {
          storageOperations.setDownloads(newDownloads);
        }
        return newDownloads;
      });
    },
    [storageOperations],
  );

  const downloadFile = (url, title) => {
    return new Promise((resolve) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      resolve();
    });
  };

  const handleStatusUpdate = useCallback(
    async (streamId, id, status) => {
      const storageDownloads = storageOperations.getDownloads();
      const newDownloads = {
        ...storageDownloads,
        [`${streamId}_${id}`]: {
          ...storageDownloads[`${streamId}_${id}`],
          status,
        },
      };
      if (status === "FAILED" || status === "COMPLETED") {
        // delete newDownloads[`${streamId}_${id}`];
      }
      storageOperations.setDownloads(newDownloads);
      setDownloads(newDownloads);
    },
    [storageOperations],
  );

  const handleWebhookStatusUpdate = useCallback(
    async ({ streamId, id, status, exportProgress }) => {
      if (status === "PROCESSING") {
        updateDownloadState(streamId, id, { exportProgress });
      } else {
        handleStatusUpdate(streamId, id, status);
      }
    },
    [handleStatusUpdate, updateDownloadState],
  );

  const exportMatchVideo = useCallback(
    async ({ streamId, type, title, refresh = false, startTime = "", endTime = "" }) => {
      try {
        const { payload } = await downloadStream({
          payload: { streamId, type, title, entityId: storageOperations.getEntityId(), startTime, endTime },
        });
        const status = payload?.data?.data?.status;
        const exportProgress = payload?.data?.data?.jobDetails?.progress ?? 0;
        const id = payload?.data?.data?.id;

        if (status === "PROCESSING" || status === "INITIATED") {
          updateDownloadState(streamId, id, {
            streamId,
            id,
            type,
            progress: 0,
            exportProgress: exportProgress || 0,
            title,
            status: "PROCESSING",
            error: null,
            startTime,
            endTime,
          });
        } else if (status === "COMPLETED") {
          showNotification({ type: "success", message: "Match video already converted!" });
        } else {
          handleStatusUpdate(streamId, id, status);
        }
        if (!refresh) setIsDownloadListVisible(true);
      } catch (error) {
        console.error(error);
      }
    },
    [downloadStream, storageOperations, handleStatusUpdate, updateDownloadState],
  );

  const getJobDetails = useCallback(
    async ({ streamId, id }) => {
      try {
        const { payload } = await getMatchJobDetails({ payload: { id, entityId: storageOperations.getEntityId() } });
        const status = payload?.data?.data?.status;
        const exportProgress = payload?.data?.data?.jobDetails?.progress ?? 0;
        const title = payload?.data?.data?.jobDetails?.title;
        const startTime = payload?.data?.data?.startTime;
        const endTime = payload?.data?.data?.endTime;

        if (status === "PROCESSING" || status === "INITIATED") {
          updateDownloadState(streamId, id, {
            streamId,
            id,
            type: "MATCH",
            progress: 0,
            exportProgress: exportProgress || 0,
            title,
            status: "PROCESSING",
            error: null,
            startTime,
            endTime,
          });
        } else {
          handleStatusUpdate(streamId, id, status);
        }
      } catch (error) {
        console.error("Failed to get job details:", error);
      }
    },
    [getMatchJobDetails, storageOperations, handleStatusUpdate, updateDownloadState],
  );

  useEffect(() => {
    const savedDownloads = storageOperations.getDownloads();
    Object.entries(savedDownloads).forEach(([id, download]) => {
      if (download?.id) getJobDetails({ id: download?.id, streamId: download?.streamId });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopDownloading = useCallback(() => {
    setDownloads({});
    storageOperations.setDownloads({});
  }, [storageOperations]);

  return (
    <DownloadMatchContext.Provider
      value={{
        downloads,
        stopDownloading,
        exportMatchVideo,
        isDownloadListVisible,
        toggleDownloadList,
        handleWebhookStatusUpdate,
        updateDownloadState,
      }}
    >
      {children}
    </DownloadMatchContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = { getStreamDownloadLink, downloadStream, getMatchJobDetails };

export default connect(mapStateToProps, mapDispatchToProps)(DownloadMatchProvider);
